import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const DEFAULT_SHORT_CALL_SECONDS = 10;

const metricDefaults = {
  loggedInSeconds: 0,
  skillsetTalkSeconds: 0,
  dnTalkSeconds: 0,
  notReadySeconds: 0,
  breakSeconds: 0,
  ringSeconds: 0,
  idleSeconds: 0,
  walkawaySeconds: 0,
  presented: 0,
  answered: 0,
  shortCallsAnswered: 0,
  dnCalls: 0,
  confOut: 0,
  transOut: 0,
  returnToQueue: 0,
  dueToTimeout: 0,
  avgSkillsetTalkSeconds: 0,
  workPercent: 0,
};

const metricColumns = [
  { key: 'loggedInSeconds', label: 'Logged In Time', type: 'time' },
  { key: 'skillsetTalkSeconds', label: 'Skillset Talk Time', type: 'time' },
  { key: 'dnTalkSeconds', label: 'DN Talk Time', type: 'time' },
  { key: 'notReadySeconds', label: 'Not Ready Time', type: 'time' },
  { key: 'breakSeconds', label: 'Break Time', type: 'time' },
  { key: 'ringSeconds', label: 'Ring Time', type: 'time' },
  { key: 'idleSeconds', label: 'Idle Time', type: 'time' },
  { key: 'walkawaySeconds', label: 'Walkaway Time', type: 'time' },
  { key: 'presented', label: 'Presented' },
  { key: 'answered', label: 'Ans' },
  { key: 'shortCallsAnswered', label: 'Short Calls Ans' },
  { key: 'dnCalls', label: 'DN Calls' },
  { key: 'confOut', label: 'Conf Out' },
  { key: 'transOut', label: 'Trans Out' },
  { key: 'returnToQueue', label: 'Rtn To Que' },
  { key: 'dueToTimeout', label: 'due to Timeout' },
];

const DEFAULT_SQL = `-- Agent Performance source tables:
-- agentstate: login/ready/not-ready/break/walkaway duration
-- call_state_log: talk time, answered calls, DN calls, transfer counts
-- call_queue_agent_log: presented queue offers, ring time, timeout/no-answer offers
SELECT * FROM agentstate WHERE eventtime >= :start_date::date AND eventtime < (:end_date::date + INTERVAL '1 day');
SELECT * FROM call_state_log WHERE created_at >= :start_date::date AND created_at < (:end_date::date + INTERVAL '1 day');
SELECT * FROM call_queue_agent_log WHERE offeredat >= :start_date::date AND offeredat < (:end_date::date + INTERVAL '1 day');`;

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const readNumber = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return Number(value) || fallback;
    }
  }
  return fallback;
};

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();

const formatDecimal = (value) => (Number(value) || 0).toFixed(2);

const formatHms = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remSeconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`;
};

const formatMetric = (row, column) => {
  const value = row?.[column.key] ?? 0;
  if (column.type === 'time') return formatHms(value);
  return formatNumber(value);
};

const metricValues = (row = {}) => metricColumns.map((column) => formatMetric(row, column));

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeMetrics = (source = {}) => ({
  loggedInSeconds: readNumber(source, ['loggedInSeconds', 'logged_in_seconds', 'LoggedInSeconds']),
  skillsetTalkSeconds: readNumber(source, ['skillsetTalkSeconds', 'skillset_talk_seconds', 'SkillsetTalkSeconds']),
  dnTalkSeconds: readNumber(source, ['dnTalkSeconds', 'dn_talk_seconds', 'DnTalkSeconds']),
  notReadySeconds: readNumber(source, ['notReadySeconds', 'not_ready_seconds', 'NotReadySeconds']),
  breakSeconds: readNumber(source, ['breakSeconds', 'break_seconds', 'BreakSeconds']),
  ringSeconds: readNumber(source, ['ringSeconds', 'ring_seconds', 'RingSeconds']),
  idleSeconds: readNumber(source, ['idleSeconds', 'idle_seconds', 'IdleSeconds']),
  walkawaySeconds: readNumber(source, ['walkawaySeconds', 'walkaway_seconds', 'WalkawaySeconds']),
  presented: readNumber(source, ['presented', 'Presented']),
  answered: readNumber(source, ['answered', 'Answered', 'ans', 'Ans']),
  shortCallsAnswered: readNumber(source, ['shortCallsAnswered', 'short_calls_answered', 'ShortCallsAnswered']),
  dnCalls: readNumber(source, ['dnCalls', 'dn_calls', 'DnCalls']),
  confOut: readNumber(source, ['confOut', 'conf_out', 'ConfOut']),
  transOut: readNumber(source, ['transOut', 'trans_out', 'TransOut']),
  returnToQueue: readNumber(source, ['returnToQueue', 'return_to_queue', 'ReturnToQueue']),
  dueToTimeout: readNumber(source, ['dueToTimeout', 'due_to_timeout', 'DueToTimeout']),
  avgSkillsetTalkSeconds: readNumber(source, ['avgSkillsetTalkSeconds', 'avg_skillset_talk_seconds', 'AvgSkillsetTalkSeconds']),
  workPercent: readNumber(source, ['workPercent', 'work_percent', 'WorkPercent']),
});

const normalizeDailyRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date),
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  agentName: normalizeText(source.agentName ?? source.agent_name ?? source.AgentName),
  ...metricDefaults,
  ...normalizeMetrics(source),
});

const normalizeGroup = (source = {}) => ({
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  agentName: normalizeText(source.agentName ?? source.agent_name ?? source.AgentName),
  agentLabel: normalizeText(source.agentLabel ?? source.agent_label ?? source.AgentLabel),
  summary: {
    ...metricDefaults,
    ...normalizeMetrics(source.summary || {}),
  },
  rows: Array.isArray(source.rows) ? source.rows.map(normalizeDailyRow) : [],
});

const buildReportPath = (filters) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('shortCallSeconds', String(filters.shortCallSeconds || DEFAULT_SHORT_CALL_SECONDS));
  if (filters.agent) params.set('agent', filters.agent);
  if (filters.direction) params.set('direction', filters.direction);
  return `/reports/agent-performance-report?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    const message = json?.error || json?.message || text || `Request failed: ${response.status}`;
    if (response.status === 404 && String(message).toLowerCase().includes('route')) {
      throw new Error('Laravel report API route missing. Register GET /api/reports/agent-performance-report in routes/api.php.');
    }
    throw new Error(message);
  }
  return json;
};

const AgentPerformanceReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
    agent: '',
    direction: '',
    shortCallSeconds: DEFAULT_SHORT_CALL_SECONDS,
  });
  const [report, setReport] = useState({
    totals: metricDefaults,
    agentGroups: [],
    reportInterval: '',
    siteName: 'CC',
    source: '',
    shortCallSeconds: DEFAULT_SHORT_CALL_SECONDS,
    dataIssues: [],
    eventSuggestions: [],
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = useCallback(async (nextFilters = filters) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters));
      const root = json?.data || json || {};
      setReport({
        totals: { ...metricDefaults, ...normalizeMetrics(root.totals || {}) },
        agentGroups: Array.isArray(root.agentGroups) ? root.agentGroups.map(normalizeGroup) : [],
        reportInterval: root.reportInterval || '',
        siteName: normalizeText(root.siteName || 'CC') || 'CC',
        source: normalizeText(root.source || ''),
        shortCallSeconds: Number(root.shortCallSeconds || nextFilters.shortCallSeconds || DEFAULT_SHORT_CALL_SECONDS),
        dataIssues: Array.isArray(root.dataIssues) ? root.dataIssues : [],
        eventSuggestions: Array.isArray(root.eventSuggestions) ? root.eventSuggestions : [],
        sqlOutput: root.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({ ...prev, totals: metricDefaults, agentGroups: [] }));
      setError(err?.message || 'Failed to load Agent Performance Report.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const rows = [
      ['Agent Performance'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Site Name', report.siteName],
      ['Agent Filter', filters.agent || 'All'],
      ['Direction Filter', filters.direction || 'All'],
      ['Short Call Seconds', report.shortCallSeconds],
      [],
      ['Grand Total', ...metricColumns.map((column) => column.label), 'Avg Skillset Talk Time', '% Work'],
      ['', ...metricValues(report.totals), formatHms(report.totals.avgSkillsetTalkSeconds), formatDecimal(report.totals.workPercent)],
    ];

    report.agentGroups.forEach((group) => {
      rows.push([]);
      rows.push([`Agent Name - ID: ${group.agentLabel}`]);
      rows.push(['Summary', ...metricValues(group.summary), formatHms(group.summary.avgSkillsetTalkSeconds), formatDecimal(group.summary.workPercent)]);
      rows.push(['Date', ...metricColumns.map((column) => column.label), 'Avg Skillset Talk Time', '% Work']);
      group.rows.forEach((row) => {
        rows.push([formatDate(row.date), ...metricValues(row), formatHms(row.avgSkillsetTalkSeconds), formatDecimal(row.workPercent)]);
      });
    });

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), 'Agent Performance');
    XLSX.writeFile(workbook, `agent-performance-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  return (
    <div className="cdn-report-shell agent-performance-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>Agent Performance</h1>
          <p>Database report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports')}>CDN Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/dnis-statistics')}>DNIS Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/daily-call-report')}>Daily Call</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-state-report')}>Agent State</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-login-logout-report')}>Login/Logout</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="agent-performance-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <label><span>Agent</span><input type="text" name="agent" value={filters.agent} onChange={updateField} placeholder="ID or name" /></label>
          <label>
            <span>Direction</span>
            <select name="direction" value={filters.direction} onChange={updateField}>
              <option value="">All Direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </label>
          <label><span>Short Call Sec</span><input type="number" min="1" name="shortCallSeconds" value={filters.shortCallSeconds} onChange={updateField} /></label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.agentGroups.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.agentGroups.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {report.dataIssues?.length ? (
          <div className="dnis-data-issues">
            {report.dataIssues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
        {report.eventSuggestions?.length ? (
          <div className="dnis-data-issues agent-login-suggestions">
            {report.eventSuggestions.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
      </section>

      <main className="cdn-report-paper agent-performance-report-paper">
        <section className="cdn-report-heading">
          <h2>Agent Performance</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Site Name:</strong> {report.siteName}</p>
        </section>

        <section className="agent-performance-table-section">
          <table className="agent-performance-table">
            <thead>
              <tr>
                <th></th>
                {metricColumns.map((column) => <th key={column.key}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="agent-performance-total-row">
                <td>Grand Total</td>
                {metricValues(report.totals).map((cell, index) => <td key={index}>{cell}</td>)}
              </tr>
              <tr className="agent-performance-sub-row">
                <td></td>
                <td colSpan={4}>Avg Skillset Talk Time: <strong>{formatHms(report.totals.avgSkillsetTalkSeconds)}</strong></td>
                <td colSpan={4}></td>
                <td colSpan={8}>% Work: <strong>{formatDecimal(report.totals.workPercent)}</strong></td>
              </tr>

              {report.agentGroups.length ? report.agentGroups.map((group) => (
                <React.Fragment key={group.agentLabel || group.agentId}>
                  <tr className="agent-performance-agent-row">
                    <td colSpan={metricColumns.length + 1}>Agent Name - ID: {group.agentLabel}</td>
                  </tr>
                  <tr className="agent-performance-summary-row">
                    <td></td>
                    {metricValues(group.summary).map((cell, index) => <td key={index}>{cell}</td>)}
                  </tr>
                  <tr className="agent-performance-sub-row">
                    <td></td>
                    <td colSpan={4}>Avg Skillset Talk Time: <strong>{formatHms(group.summary.avgSkillsetTalkSeconds)}</strong></td>
                    <td colSpan={4}></td>
                    <td colSpan={8}>% Work: <strong>{formatDecimal(group.summary.workPercent)}</strong></td>
                  </tr>
                  {group.rows.map((row) => (
                    <React.Fragment key={`${group.agentLabel}-${row.date}`}>
                      <tr>
                        <td>{formatDate(row.date)}</td>
                        {metricValues(row).map((cell, index) => <td key={index}>{cell}</td>)}
                      </tr>
                      <tr className="agent-performance-sub-row">
                        <td></td>
                        <td colSpan={4}>Avg Skillset Talk Time: <strong>{formatHms(row.avgSkillsetTalkSeconds)}</strong></td>
                        <td colSpan={4}></td>
                        <td colSpan={8}>% Work: <strong>{formatDecimal(row.workPercent)}</strong></td>
                      </tr>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              )) : (
                <tr>
                  <td colSpan={metricColumns.length + 1} className="cdn-empty">
                    {loading ? 'Loading report...' : 'No report data found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>Database SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default AgentPerformanceReport;
